import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, XCircle, CheckCircle2, Film, Users, Briefcase, Mail, IdCard } from "lucide-react";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../api/client";
import { toast } from "sonner";

const TABS = [
    { key: "films", label: "Films Queue", Icon: Film },
    { key: "kyc", label: "KYC Queue", Icon: IdCard },
    { key: "deals", label: "Deals", Icon: Briefcase },
    { key: "emails", label: "Email Log", Icon: Mail },
];

export default function AdminDashboard() {
    const [tab, setTab] = useState("films");
    const [stats, setStats] = useState(null);
    const [pending, setPending] = useState([]);
    const [kyc, setKyc] = useState([]);
    const [deals, setDeals] = useState([]);
    const [emails, setEmails] = useState([]);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [s, p, k, d, e] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/admin/pending-films"),
                api.get("/admin/kyc/pending"),
                api.get("/admin/deals"),
                api.get("/admin/emails"),
            ]);
            setStats(s.data);
            setPending(p.data);
            setKyc(k.data);
            setDeals(d.data);
            setEmails(e.data);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const approve = async (id, approved) => {
        try {
            await api.post(`/admin/films/${id}/approve`, { approved });
            toast.success(approved ? "Film approved" : "Film rejected");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const decideKyc = async (submissionId, approved) => {
        const reason = approved ? null : (window.prompt("Reason (shown to user):") || "Please resubmit clearer documents.");
        try {
            await api.post(`/admin/kyc/${submissionId}/decide`, { approved, reason });
            toast.success(approved ? "KYC approved" : "KYC rejected");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <Navbar />
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <div className="text-[11px] tracking-[0.3em] uppercase text-platinum mb-2 flex items-center gap-2">
                            <ShieldCheck className="w-4 h-4" /> Admin console
                        </div>
                        <h1 className="font-display text-5xl md:text-6xl leading-none">
                            Approval <span className="silver-text italic">queue.</span>
                        </h1>
                    </div>
                </div>

                {err && <div className="text-red-400 text-sm mb-4">{err}</div>}

                {/* Stats */}
                <div className="grid md:grid-cols-5 gap-4 mb-10" data-testid="admin-stats">
                    <Stat icon={Film} label="Films" value={stats?.films_total ?? "—"} />
                    <Stat icon={ShieldCheck} label="Pending" value={pending.length} highlight={pending.length > 0} />
                    <Stat icon={IdCard} label="KYC queue" value={kyc.length} highlight={kyc.length > 0} />
                    <Stat icon={Briefcase} label="Deals" value={stats?.deals_total ?? "—"} />
                    <Stat icon={Users} label="Users" value={stats?.users_total ?? "—"} />
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-6 border-b border-white/5 mb-8 text-sm uppercase tracking-widest overflow-x-auto" data-testid="admin-tabs">
                    {TABS.map(({ key, label, Icon }) => (
                        <button
                            key={key}
                            onClick={() => setTab(key)}
                            className={`flex items-center gap-2 py-4 border-b-2 whitespace-nowrap transition-colors ${
                                tab === key ? "border-platinum text-platinum" : "border-transparent text-silver-500 hover:text-white"
                            }`}
                            data-testid={`admin-tab-${key}`}
                        >
                            <Icon className="w-4 h-4" />
                            {label}
                        </button>
                    ))}
                </div>

                {loading && <div className="text-silver-500 animate-pulse">Loading…</div>}

                {!loading && tab === "films" && (
                    <section className="glass rounded-sm overflow-hidden" data-testid="pending-queue">
                        {pending.length === 0 ? (
                            <div className="p-10 text-center text-silver-500">All clear. No pending films.</div>
                        ) : (
                            <div className="divide-y divide-white/5">
                                {pending.map((f, i) => (
                                    <motion.div
                                        key={f.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.04 }}
                                        className="flex items-center gap-5 p-5"
                                    >
                                        {f.poster_url && <img src={f.poster_url} alt={f.title} className="w-14 h-20 object-cover rounded-sm border border-white/10" />}
                                        <div className="flex-1 min-w-0">
                                            <div className="font-display text-xl truncate">{f.title}</div>
                                            <div className="text-xs text-silver-500 mt-1 uppercase tracking-widest">
                                                {f.country} · {f.language} · {f.creator_name}
                                            </div>
                                            <div className="text-xs text-silver-400 mt-1">
                                                Deed {f.deed_signed ? "signed ✓" : "unsigned"} · {(f.chain_of_title_docs || []).length} COT docs
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => approve(f.id, false)}
                                            className="text-silver-400 hover:text-red-400 flex items-center gap-1 text-xs uppercase tracking-widest px-3 py-2 border border-white/10 rounded-sm"
                                            data-testid={`reject-${f.id}`}
                                        >
                                            <XCircle className="w-4 h-4" /> Reject
                                        </button>
                                        <button
                                            onClick={() => approve(f.id, true)}
                                            disabled={!f.deed_signed}
                                            className="bg-platinum hover:bg-platinum-200 text-ink-300 flex items-center gap-2 text-xs uppercase tracking-widest px-4 py-2 rounded-sm disabled:opacity-40"
                                            data-testid={`approve-${f.id}`}
                                        >
                                            <CheckCircle2 className="w-4 h-4" /> Approve
                                        </button>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </section>
                )}

                {!loading && tab === "kyc" && (
                    <section className="glass rounded-sm overflow-hidden" data-testid="kyc-queue">
                        {kyc.length === 0 ? (
                            <div className="p-10 text-center text-silver-500">No KYC submissions in review.</div>
                        ) : (
                            <div className="divide-y divide-white/5">
                                {kyc.map((k) => (
                                    <div key={k.id} className="flex flex-wrap items-center gap-5 p-5">
                                        <div className="flex-1 min-w-[240px]">
                                            <div className="font-display text-lg">{k.user?.name || k.user?.email}</div>
                                            <div className="text-xs text-silver-500 uppercase tracking-widest mt-1">
                                                {k.user?.role} · {k.user?.company || "—"} · {k.doc_type.replace("_", " ")}
                                            </div>
                                            <div className="text-[11px] text-silver-400 mt-1">{k.doc_name} · submitted {new Date(k.submitted_at).toLocaleString()}</div>
                                        </div>
                                        {k.doc_data_url && (
                                            <a
                                                href={k.doc_data_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-platinum text-xs uppercase tracking-widest border border-white/10 hover:border-platinum px-3 py-2 rounded-sm"
                                                data-testid={`kyc-view-${k.id}`}
                                            >
                                                View Document
                                            </a>
                                        )}
                                        <button
                                            onClick={() => decideKyc(k.id, false)}
                                            className="text-silver-400 hover:text-red-400 flex items-center gap-1 text-xs uppercase tracking-widest px-3 py-2 border border-white/10 rounded-sm"
                                            data-testid={`kyc-reject-${k.id}`}
                                        >
                                            <XCircle className="w-4 h-4" /> Reject
                                        </button>
                                        <button
                                            onClick={() => decideKyc(k.id, true)}
                                            className="bg-platinum hover:bg-platinum-200 text-ink-300 flex items-center gap-2 text-xs uppercase tracking-widest px-4 py-2 rounded-sm"
                                            data-testid={`kyc-approve-${k.id}`}
                                        >
                                            <CheckCircle2 className="w-4 h-4" /> Verify
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}

                {!loading && tab === "deals" && (
                    <section className="glass rounded-sm overflow-hidden" data-testid="admin-deals">
                        {deals.length === 0 ? (
                            <div className="p-10 text-center text-silver-500">No deals yet.</div>
                        ) : (
                            <div className="divide-y divide-white/5">
                                {deals.map((d) => (
                                    <div key={d.id} className="flex items-center gap-5 p-5 text-sm">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-white truncate">{d.film_title}</div>
                                            <div className="text-silver-500 text-xs mt-0.5 uppercase tracking-widest">
                                                {d.buyer_name} · {d.buyer_company}
                                            </div>
                                        </div>
                                        <div className="text-platinum">${Number(d.price_usd).toLocaleString()}</div>
                                        <div className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border ${
                                            d.status === "transfer_complete"
                                                ? "text-emerald-300 border-emerald-400/40"
                                                : "text-silver-300 border-white/10"
                                        }`}>
                                            {d.status.replace("_", " ")}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}

                {!loading && tab === "emails" && (
                    <section className="glass rounded-sm overflow-hidden" data-testid="admin-emails">
                        {emails.length === 0 ? (
                            <div className="p-10 text-center text-silver-500">No emails sent yet.</div>
                        ) : (
                            <div className="divide-y divide-white/5 max-h-[600px] overflow-y-auto">
                                {emails.map((e) => (
                                    <div key={e.id} className="p-4 text-sm">
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="text-silver-200 text-sm">{e.subject}</div>
                                            <div className="text-[11px] text-silver-500 tracking-widest uppercase">{e.kind}</div>
                                        </div>
                                        <div className="text-[11px] text-silver-500">
                                            to <span className="text-silver-300">{e.to_email}</span> · {new Date(e.sent_at).toLocaleString()}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}
            </div>
        </div>
    );
}

function Stat({ icon: Icon, label, value, highlight }) {
    return (
        <div className={`glass rounded-sm p-5 ${highlight ? "border-platinum/40" : ""}`}>
            <div className="flex items-center gap-2 text-silver-400 text-[11px] uppercase tracking-widest mb-3">
                <Icon className={`w-4 h-4 ${highlight ? "text-platinum" : "text-platinum"}`} />
                {label}
            </div>
            <div className={`font-display text-4xl ${highlight ? "text-platinum" : "text-white"}`}>
                {value}
            </div>
        </div>
    );
}
